import React from 'react';

import { colors, semanticColors } from '@/assets/styles/colors';
import PageLayout from '@/components/layout/PageLayout';
import { PermissionGuard } from '@/components/layout';
import { Card } from '@/components/ui/cards';
import Table from '@/components/ui/tables/Table';
import { PrimaryButton } from '@/components/ui/buttons';
import { DateRangePicker, FilterDropdown } from '@/components/ui/filters';
import { ExportDropdown } from '@/components/ui/filters/ExportDropdown';
import { IconScout } from '@/components/ui/images/IconScout';
import { Text } from '@/components/ui/text/Text';
import { EmployeeDimensionalFilter } from '@/components/features/attendance/EmployeeDimensionalFilter';
import { useTranslation } from '@/i18n';
import { useMarksReport } from '@/hooks/attendance/report/useMarksReport';
import { formatDateToYYYYMMDD } from '@/utils';


const MAX_HISTORY_YEARS = 5;

export const MarksReport: React.FC = () => {
  const { t: translate } = useTranslation();
  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    columns,
    actions,
    onSortChange,
    onDateRangeChange,
    dateFrom,
    dateTo,
    filterDefinitions,
    filterValues,
    onFilterChange,
    isLoading,
    canDownload,
    onDownload,
    isExporting,
    dimensionalConfig,
    dimensionalFilter,
    onAdvancedSearch,
    bulkActions,
    tableSelectionKey,
    onSelectionChange,
  } = useMarksReport();

  const today = new Date();
  const maxDate = formatDateToYYYYMMDD(today);
  const minDateValue = new Date(today);
  minDateValue.setFullYear(minDateValue.getFullYear() - MAX_HISTORY_YEARS);
  const minDate = formatDateToYYYYMMDD(minDateValue);

  return (
    <PageLayout>
      <div className="flex flex-col gap-6" data-testid="marks-report-list-page">
        <Card>
          <div className="flex flex-col gap-4 p-4" data-testid="marks-report-filters">
            <div className="flex flex-wrap items-end gap-3">
              <DateRangePicker
                label={translate('attendance:marksReport.filters.dateRange')}
                value={{ from: dateFrom, to: dateTo }}
                onChange={onDateRangeChange}
                maxDate={maxDate}
                minDate={minDate}
              />

              <FilterDropdown
                filters={filterDefinitions}
                values={filterValues}
                onChangeFilter={onFilterChange}
              />
            </div>

            <button
              type="button"
              onClick={dimensionalFilter.toggleExpanded}
              className="self-start flex items-center gap-1 font-medium text-sm cursor-pointer bg-transparent border-none p-0"
              style={{ color: colors.important.main }}
              aria-expanded={dimensionalFilter.isExpanded}
              data-testid="marks-report-advanced-filters-toggle"
            >
              {dimensionalFilter.isExpanded
                ? translate('attendance:dimensionalFilter.hide')
                : translate('attendance:dimensionalFilter.showAdvancedFilters')}
              <IconScout
                name={dimensionalFilter.isExpanded ? 'angleUp' : 'angleDown'}
                size={16}
              />
            </button>

            {dimensionalFilter.isExpanded && (
              <>
                <EmployeeDimensionalFilter
                  config={dimensionalConfig}
                  state={dimensionalFilter}
                />
                <PrimaryButton
                  onClick={onAdvancedSearch}
                  data-testid="marks-report-advanced-search-button"
                  className="self-start"
                >
                  {translate('common:actions.search')}
                </PrimaryButton>
              </>
            )}
          </div>
        </Card>

        <PermissionGuard action="view">
          <div className="flex justify-end">
            <ExportDropdown
              onExport={onDownload}
              placement="right"
              disabled={!canDownload}
              isLoading={isExporting}
              availableFormats={['EXCEL']}
            />
          </div>
        </PermissionGuard>

        <Table
          key={tableSelectionKey}
          columns={columns}
          actions={actions}
          data={rows}
          totalRows={total}
          isLoading={isLoading}
          serverSideSort
          onSortChange={onSortChange}
          selectable
          onSelectionChange={onSelectionChange}
          bulkActions={bulkActions}
          pagination={{
            page,
            pageSize,
            total,
            onChange: setPage,
            onPageSizeChange: setPageSize,
          }}
          emptyMessage={translate('attendance:marksReport.messages.empty')}
          data-testid="marks-report-table"
        />

        <div
          className="flex items-center gap-2 text-sm"
          data-testid="marks-report-pending-legend"
        >
          <Text
            as="span"
            style={{
              display: 'inline-block',
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: semanticColors.status.warning,
            }}
          >
            {null}
          </Text>
          <Text as="span" variant="small-regular" color={colors.grays.neutral66}>
            {translate('attendance:marksReport.adjust.pendingIndicator.legend')}
          </Text>
        </div>
      </div>
    </PageLayout>
  );
};

export default MarksReport;
